var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { AwsUi } from '../../../core/runtime/src';
import logger from '../../../core/utils/src/logger';
import { Dropdown } from '../../dropdown/src';
import { getFlatOptions, getHighlightedId } from '../../dropdown/src/utils/options';
import DropdownFooter from '../../dropdown/src/utils/dropdown-footer';
import AutosuggestController from './controllers/autosuggest-controller';
import { filterOptions } from './utils/utils';
import BaseInput from './base-input';
export var Autosuggest = AwsUi.__addComponent('awsui-autosuggest', {
    releaseStatus: 'stable',
    callbacks: {
        initialized: function () {
            this.__initializeController();
            if (!this.controlId) {
                this.controlId = this.__controller.generatedId;
            }
        },
        attached: function () {
            this.__controller.addListeners();
        },
        detached: function () {
            this.__controller.removeListeners();
        }
    },
    properties: {
        // Text entered into the form element.
        value: {
            type: 'string',
            defaultValue: '',
            onChange: function () {
                this.__controller.valueAttributeChangeHandler(this.value);
            }
        },
        // An array of options that will be displayed to the user in the form of a dropdown. This list should provide
        // the user with suggested values based on the user's input. Each option has the following properties (only `value` is mandatory):
        //
        // * `value` [string]: The value that will be put into the input field when the option is selected.
        // * `label` [string]: A short title shown in the dropdown to describe the option.
        // * `labelTag` [string]: A tag that provides additional guidance, shown next to the label.
        // * `description` [string]: Further explanatory guidance about the option, shown below the label.
        // * `disabled` [boolean]: Whether the option is disabled.
        // * `iconName` [string]: Name of the Polaris icon to display on the left of the option. For custom icons use `iconAlt` and `iconUrl`.
        // * `iconUrl` [string]: The URL for a custom icon.
        // * `iconAlt` [string]: The alternative text for a custom icon.
        // * `tags` [string array]: A list of tags giving further guidance about the option.
        // * `filteringTags` [string array]: A list of metadata that is only displayed when one of the tags matches the user's input.
        //
        // Options can be grouped by specifying the given option as a group of options:
        //
        // * `label` [string]: description of the group.
        // * `options` [array]: array of simple options.
        // * `disabled` [boolean]: whether the whole group is disabled.
        //
        // Notice that only one level of options nesting is supported.
        //
        // If you would like to use the built-in filtering capabilities of this component, simply provide
        // a list of all valid options here and they will be automatically filtered based on the input
        // of the user.
        // Alternatively, you can listen to the `input` or `delayedInput` event and set new options
        // on your own.
        options: {
            type: 'array',
            itemTypes: ['Autosuggest.IOption', 'Autosuggest.Option', 'Autosuggest.OptionsGroup'],
            onChange: function () {
                this.__controller.optionsAttributeChangeHandler(this.options);
            }
        },
        // Disables the default filtering mechanism that automatically only displays options that match the current input value.
        //
        // **Deprecated:** Use `filteringType` instead.
        disableDefaultFiltering: {
            type: 'boolean',
            defaultValue: false,
            onChange: function (oldValue, newValue) {
                if (newValue) {
                    logger.warnOnce('[awsui-autosuggest]', "The `disableDefaultFiltering` property has been deprecated. Use `filteringType=\"auto\"` instead.");
                }
            }
        },
        // Determines how filtering is applied to the list of `options`:
        // * `auto`: component will automatically filter options based on user input.
        // * `manual`: set up an `input` or `delayedInput` event listener and filter items on your side or request
        // them from server
        //
        // By default the component will filter the provided `options` based on the value of the input field.
        // Only options that have a `value` or `label` which contains the input value as a substring
        // will be displayed in the list of suggestions.
        //
        // If you set this property to `manual` this default filtering mechanism will be disabled, hence all provided `options` will be
        // displayed in the list of suggestions. In that case make sure that you listen to the `input` or `delayedInput` event in order
        // to set the `options` property to the suggestions that are relevant for the user, given the input value.
        //
        // It should also be noted that manual filtering will not disable match highlighting.
        filteringType: {
            type: 'string',
            valid: ['auto', 'manual'],
            defaultValue: 'auto'
        },
        // Setting this text will cause it to be displayed in the footer of the dropdown when `statusType` is also set.
        // Use this to indicate that more options are being loaded, or that an error occurred while loading.
        //
        // **Deprecated:** Use `loadingText`, `finishedText`, `errorText` and `retryText` instead.
        statusText: {
            type: 'string',
            onChange: function (oldValue, newValue) {
                if (newValue) {
                    logger.warnOnce('[awsui-autosuggest]', "The `statusText` property has been deprecated. Use `loadingText`, `finishedText`, `errorText` and `retryText` instead.");
                }
            }
        },
        // Text to be displayed when in loading state.
        loadingText: {
            type: 'string'
        },
        // Text to be displayed at the bottom of the dropdown menu once pagination has reached the end.
        finishedText: {
            type: 'string'
        },
        // Text to be displayed in case of a data fetching error. Make sure to also provide the `recoveryText`,
        // see its documentation for more details.
        errorText: {
            type: 'string'
        },
        // Text for the recovery button. Displayed next to the error text.
        // Use the `recoveryClick` event to do a recovery action, for example, retrying the request.
        recoveryText: {
            type: 'string'
        },
        // Specifies the current status of loading more suggestions.
        //
        // * `pending`: no request in progress, but there may be more items loaded.
        // * `loading`: data fetching is in progress.
        // * `finished`: pagination reached the end, no more requests expected.
        // * `error`: an error occurred during fetch. You should provide user an option to recover, see examples for more details.
        statusType: {
            type: 'string',
            nullable: true,
            valid: ['pending', 'loading', 'finished', 'error'],
            defaultValue: 'finished'
        },
        // Determines whether the status message in the footer should be sticky,
        // i.e. always visible regardless of scrolling position in the dropdown.
        // The property is `true` by default to reflect the legacy behavior of Autosuggest.
        // However, it is recommended to set this property to `false`.
        // See the code example in the API tab for more details.
        stickyFooter: {
            type: 'boolean',
            defaultValue: true
        },
        // The name of the control used in HTML forms.
        name: {
            type: 'string'
        },
        // Placeholder text rendered when the value is an empty string.
        placeholder: {
            type: 'string'
        },
        // Specifies that the input should be disabled, preventing the user from
        // modifying the value and preventing the value from being included in a
        // form submission. A disabled input cannot receive focus.
        //
        // **AngularJS:** Use `data-disabled` instead.
        disabled: {
            type: 'boolean'
        },
        // Specifies that the input should be readonly, preventing the user from
        // modifying the value but including it in a form submission. A readonly input can receive focus.
        //
        // Do not use readonly inputs outside of a form.
        readonly: {
            type: 'boolean'
        },
        // Overrides invalidation state. Usually invalid state comes from wrapping `awsui-form-field` component, however
        // sometimes you need to override its state when you have more than one input within single form-field.
        invalid: {
            type: 'boolean',
            nullable: true
        },
        // Setting this to `true` will disable any native browser capabilities to
        // automatically correct user input, such as `autocorrect` and `autocapitalize`.
        // If unset, behavior will be the default behavior of the respective browser.
        disableBrowserAutocorrect: {
            type: 'boolean',
            defaultValue: false
        },
        // Allows you to indicate that the control is to be focused as
        // soon as the `load` event triggers, allowing the user to just start
        // typing without having to manually focus the input. Don't use this option
        // in pages that allow for the field to be scrolled out of the viewport.
        autofocus: {
            type: 'boolean'
        },
        // Id of the internal input. Useful for relating a label element's
        // "for" attribute to this control. It defaults to an automatically generated id.
        controlId: {
            type: 'string',
            announceValue: true,
            onChange: function () {
                if (!this.controlId) {
                    this.controlId = this.__controller.generatedId;
                }
            }
        },
        // Adds an aria label on the native input.
        label: {
            type: 'string'
        },
        // Adds aria-labelledby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define a custom id inside the label
        // 2. Refer to that label from every single control under that label using this property
        ariaLabelledby: {
            type: 'string'
        },
        // Adds aria-describedby on the native input. Use this only with form fields that
        // contain multiple controls under the same label.
        // 1. Define custom ids inside the description, hint and error text
        // 2. Refer to these from every single control under that label using this property
        // 3. Refer to any other hint/description text that you provide
        ariaDescribedby: {
            type: 'string',
            nullable: true
        },
        // Adds aria-required on the native input
        ariaRequired: {
            type: 'boolean'
        },
        // Properties for the controller interface:
        // Keeps track of the currently highlighted option
        __highlightedOption: {
            type: 'object',
            objectType: 'Autosuggest.Option'
        },
        // The text to highlight in options. If it's set, it will be
        // used, instead of this.value, to highlight the options.
        __highlightText: {
            type: 'string'
        },
        // Whether the dropdown is currently open
        __open: {
            type: 'boolean',
            defaultValue: false
        },
        // A default suggested aria-describedby value that can be pushed down from a FormField
        __suggestedAriaDescribedby: {
            type: 'string'
        },
        // Whether to always open the dropdown on focus
        __openOnFocus: {
            type: 'boolean',
            defaultValue: false
        }
    },
    events: {
        // Mimics the native `input` event on inputs.
        // The event `detail` contains the current value of the field.
        //
        // Use this event if you would like to update the list of options yourself. Simply use the `value`
        // from the event details to generate a new list of suggestions and update the `options` property.
        //
        // If you are retrieving new suggestions from an asynchronous API, please use the `delayedInput`
        // event to trigger the API request, and only use the `input` event to set the loading state.
        input: {
            bubbles: true,
            cancelable: false,
            detailType: 'Input.ChangeDetail'
        },
        // Fired after the user has changed the value of the input field and stopped typing for a certain
        // period of time.
        // If you do not want to use a fixed set of options, use this event to generate new suggestions
        // based on the user's input, especially when you are requesting the suggestions from an asynchronous
        // API.
        delayedInput: {
            bubbles: true,
            cancelable: false,
            detailType: 'Input.ChangeDetail'
        },
        // Fired when `statusType` is set to `pending` and there is a need for more items:
        // * When the user has scrolled to the bottom of the dropdown.
        // * When the options property changed and there are not enough items to make the list scrollable.
        loadMore: {
            bubbles: true,
            cancelable: false
        },
        // Fired when the user clicks the recovery button placed at the bottom of the dropdown list in the error state.
        // Use this to retry a failed request or provide another option for the user to recover from the error.
        recoveryClick: {
            bubbles: true,
            cancelable: false
        },
        // Mimics the native `change` event on inputs.
        // The event `detail` contains the current value of the field, as well as the `selectedOption` if it
        // comes from a suggestion in the dropdown.
        change: {
            bubbles: true,
            cancelable: false,
            detailType: 'Autosuggest.ChangeDetail'
        },
        // Fired when input focus is set onto the UI control.
        focus: {
            bubbles: true,
            cancelable: false
        },
        // Fired when input focus is removed from the UI control.
        blur: {
            bubbles: true,
            cancelable: false
        },
        // Fired when the underlying native input fires `keydown` event.
        // The event `detail` contains the `keyCode` and information about modifiers (alt, ctrl, ...).
        keydown: {
            bubbles: true,
            cancelable: true,
            detailType: 'Input.KeyDetail'
        },
        // Fired when the underlying native input fires `keyup` event.
        // The event `detail` contains the `keyCode` and information about modifiers (alt, ctrl, ...).
        keyup: {
            bubbles: true,
            cancelable: true,
            detailType: 'Input.KeyDetail'
        },
        // Fired when the underlying native input fires `keypress` event.
        // The event `detail` contains the `key` property and information about modifiers (alt, ctrl, ...).
        keypress: {
            bubbles: true,
            cancelable: true,
            detailType: 'Input.KeyDetail'
        },
        // Fired when an option from dropdown gets selected, either by handling the selection handler from option or from the keydown event.
        // The event `detail` contains the properties `value` and optionally `label`.
        __selectOption: {
            bubbles: true,
            cancelable: true,
            detailType: 'Autosuggest.SelectOptionDetail'
        }
    },
    regions: {
        // Displayed when there are no suggestions to display.
        // This will only be shown when `statusType` is set to `finished` or not set at all.
        empty: {}
    },
    functions: {
        // Focuses the input field of this component
        focus: function () {
            this.node.querySelector('input').focus();
        },
        // Filters the current options if necessary and returns a list of valid options
        __filterOptions: function () {
            if (this.filteringType === 'manual' || this.disableDefaultFiltering) {
                // We assume that the options have already been filtered
                return this.options;
            }
            return filterOptions(this.options, this.value || '');
        },
        // Initialize the controller.
        // Having this as an intermediate step makes sure that we actually implement all
        // functions required by the interface, even if it changes.
        __initializeController: function () {
            var _this = this;
            this.__controller = new AutosuggestController({
                openDropdown: function () { return _this.__openDropdown(); },
                closeDropdown: function () { return _this.__closeDropdown(); },
                resetDropdownScroll: function () {
                    // The dropdown might be open and scrolled down at this point. We have reset
                    var dropdown = _this.node.querySelector('awsui-select-dropdown');
                    if (dropdown) {
                        dropdown.component.__resetScrollTop();
                    }
                },
                isDropdownOpen: function () { return _this.__open; },
                getEnabledOptions: function () { return getFlatOptions(_this.__filterOptions(), false); },
                getHighlightedOption: function () { return _this.__highlightedOption; },
                setHighlightedOption: function (option) { return _this.__setHighlightedOption(option); },
                setSelectedOption: function (option, onNotCancelled) {
                    return _this.__selectOption(option, onNotCancelled);
                },
                notifyDelayedInput: function () { return _this.__fireEvent('delayedInput', { value: _this.value }); },
                getValue: function () { return _this.value; },
                shouldOpenOnFocus: function () { return _this.__openOnFocus; },
                notifyInput: function (value) {
                    _this.value = value;
                    _this.__fireEvent('input', { value: value });
                },
                notifyChange: function (value, selectedOption) {
                    var details = { value: value };
                    if (selectedOption) {
                        details.selectedOption = selectedOption;
                    }
                    _this.__fireEvent('change', details);
                },
                focus: function () { return _this.focus(); },
                notifyFocus: function () { return _this.__fireEvent('focus'); },
                notifyBlur: function () { return _this.__fireEvent('blur'); },
                notifyKeyEvent: function (type, detail, extra) { return _this.__fireEvent(type, detail, extra); }
            }, this.node);
        },
        // Opens the dropdown if possible
        __openDropdown: function () {
            if (this.disabled || this.readonly) {
                return;
            }
            this.__open = true;
        },
        // Closes the dropdown
        __closeDropdown: function () {
            this.__open = false;
            this.__highlightedOption = null;
        },
        // Called when an option has been selected.
        __selectOption: function (option, onNotCancelled) {
            this.__highlightedOption = null;
            this.__fireEvent('__selectOption', { selectedOption: option }, {
                onNotCanceled: function () { return onNotCancelled(); }
            });
        },
        __setHighlightedOption: function (option) {
            this.__highlightedOption = option;
        }
    },
    wrapperSupport: {
        ngModel: {
            value: 'value',
            event: 'input'
        }
    },
    render: function (m, h) {
        var _this = this;
        var dropdownId = this.controlId ? this.controlId + "-dropdown" : undefined;
        var suggestions = this.__filterOptions();
        // Fall back to the default value because setting the property explicitly to null will not surface the defaultValue.
        var statusType = this.statusType || 'finished';
        var searchAttributes = this.__controller.getSearchAttributesForBaseInput(!this.disabled && !this.readonly && this.value);
        var footer = statusType && (AwsUi.__createElement(DropdownFooter, { status: statusType, isEmpty: suggestions.length === 0, emptyText: h.region('empty', 'span'), loadingText: this.loadingText || (statusType === 'loading' && this.statusText), errorText: this.errorText || (statusType === 'error' && this.statusText), finishedText: this.finishedText, recoveryText: this.recoveryText, onRecoveryClick: function () { return _this.__fireEvent('recoveryClick'); } }));
        var dropdownMouseDown = function () { return (_this.__componentExtensionClicked = true); };
        var dropdownMouseUp = function () { return _this.focus(); };
        var ariaStatus = function () { return (AwsUi.__createElement("div", { role: "status", "aria-live": "polite", "aria-atomic": "false", className: "awsui-util-hide awsui-autosuggest-aria-status" },
            suggestions.length === 0 ? h.region('empty', 'span') : null,
            " ",
            statusType ? _this.statusText : null)); };
        var loadMoreHandler = function (event) {
            event.stopPropagation();
            if (_this.options.length > 0 && statusType === 'pending') {
                _this.__fireEvent('loadMore');
            }
        };
        return (AwsUi.__createElement("div", null,
            ariaStatus(),
            AwsUi.__createElement("div", { class: "awsui-dropdown-trigger" },
                AwsUi.__createElement(BaseInput, __assign({}, searchAttributes, { value: this.value, type: "search", autocomplete: false, disabled: this.disabled, readonly: this.readonly, disableBrowserAutocorrect: this.disableBrowserAutocorrect, "aria-invalid": this.invalid, "aria-required": this.ariaRequired, 
                    // This component uses roles and aria-attributes in accordance to WAI-ARIA 1.0 combobox specification
                    // https://www.w3.org/TR/wai-aria-practices/#combobox
                    // newer 1.1 version is not yet supported by VoiceOver so we stick to the older one
                    additionalAttrs: {
                        name: this.name,
                        id: this.controlId,
                        placeholder: this.placeholder,
                        autofocus: this.autofocus,
                        role: 'combobox',
                        'aria-owns': this.__open && dropdownId,
                        'aria-expanded': "" + this.__open,
                        'aria-activedescendant': getHighlightedId(dropdownId, suggestions, this.__highlightedOption),
                        'aria-haspopup': 'listbox',
                        'aria-autocomplete': 'list',
                        'aria-describedby': this.ariaDescribedby || this.__suggestedAriaDescribedby || undefined,
                        'aria-label': this.label,
                        'aria-labelledby': this.ariaLabelledby
                    } }))),
            AwsUi.__createElement("div", { onmousedown: dropdownMouseDown.bind(this), onmouseup: dropdownMouseUp.bind(this) },
                AwsUi.__createElement(Dropdown, { options: suggestions, open: this.__open, highlightedOption: this.__highlightedOption, footer: this.stickyFooter ? footer : null, controlId: dropdownId, highlightText: this.__highlightText || this.value, hasFiltering: true, loadMore: loadMoreHandler, listBottom: this.stickyFooter ? null : footer }))));
    }
});
//# sourceMappingURL=autosuggest.js.map